import { Link } from 'react-router-dom';
import { Wrench } from 'lucide-react';
import Layout from '../components/Layout';
import { Card } from '../components/ui';

interface MaintenancePageProps {
  /** 終了予定時刻（ISO8601）。null なら時刻を出さない */
  endsAt?: string | null;
  /** 管理画面から入力した追記メッセージ */
  message?: string | null;
}

/**
 * メンテナンス中のゲートページ。
 * 管理画面のトグル（maintenance_state）が有効な間、機能ページはこのページを表示する。
 *
 * 「処理中の書き起こしは失われない」ことを明示するのが最重要。メンテ中に画面が
 * 使えないと、利用者はアップロード済みの音声が消えたのではないかと不安になるため。
 */
export default function MaintenancePage({ endsAt, message }: MaintenancePageProps) {
  return (
    <Layout>
      <main className="max-w-2xl mx-auto px-4 py-20">
        <Card padding="none" className="p-8 md:p-12 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 text-primary mb-6">
            <Wrench className="w-8 h-8" />
          </div>

          <h1 className="text-2xl md:text-3xl font-bold text-text-heading mb-4">
            ただいまメンテナンス中です
          </h1>

          <p className="text-text leading-relaxed mb-6">
            サービス改善のため、一時的にご利用を停止しています。
            <br />
            ご不便をおかけしますが、いましばらくお待ちください。
          </p>

          <div className="rounded-lg border border-accent/20 bg-accent/5 px-5 py-4 text-left mb-8">
            <p className="text-sm font-bold text-text-heading mb-1">
              処理中の書き起こしについて
            </p>
            <p className="text-sm text-text-sub leading-relaxed">
              メンテナンス開始時に処理中だった音声は、そのまま処理が続行されます。
              メンテナンス明けにダッシュボードからご確認ください。
            </p>
          </div>

          {endsAt && (
            <p className="text-text mb-8">
              <span className="text-text-sub text-sm block mb-1">終了予定</span>
              <time dateTime={endsAt} className="text-lg font-bold text-text-heading">
                {formatEndsAt(endsAt)}
              </time>
              <span className="text-text-sub text-sm block mt-1">
                作業状況により前後する場合があります。
              </span>
            </p>
          )}

          {message && (
            <p className="text-text leading-relaxed whitespace-pre-wrap mb-8">{message}</p>
          )}

          <Link
            to="/"
            className="inline-flex items-center justify-center px-6 py-3 rounded-lg bg-primary text-white font-semibold hover:bg-primary-hover transition-colors"
          >
            トップページに戻る
          </Link>

          <p className="mt-8 text-sm text-text-sub">
            最新情報は{' '}
            <a
              href="https://twitter.com/otomochi_com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:underline"
            >
              公式X（Twitter）
            </a>{' '}
            でお知らせします。
          </p>
        </Card>
      </main>
    </Layout>
  );
}

/**
 * 終了予定時刻を日本語表記にする（例: 8月11日(月) 3:00頃）。
 * 日付が不正な場合は表示しない側に倒す（呼び出し側で空文字を描画しない）。
 */
export function formatEndsAt(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';

  const formatted = new Intl.DateTimeFormat('ja-JP', {
    month: 'long',
    day: 'numeric',
    weekday: 'short',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'Asia/Tokyo',
  }).format(date);

  return `${formatted}頃`;
}
